import { Feature } from '@mondaygpt/constants'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'types'
import { logout } from '../../authentication/slices/authentication-view.slice'
import { userLimitationsApi } from '../services/user-limitations.api'

export interface FeatureUsageResult {
  isAllowed: boolean
  currentUsage: number
  limit?: number
  resetWindowMinutes?: number
  nextResetAt?: string // ISO date string
  remaining?: number
  softLimitReached?: boolean
}

export interface UserLimitationsState {
  limitations: Record<Feature, FeatureUsageResult>
  loading: boolean
  error?: string
}

const initialState: UserLimitationsState = {
  limitations: {} as Record<Feature, FeatureUsageResult>,
  loading: true,
}

const userLimitationsSlice = createSlice({
  name: 'userLimitations',
  initialState,
  reducers: {
    updateUsage: (
      state,
      action: PayloadAction<{ feature: Feature; usage: FeatureUsageResult }>
    ) => {
      const { feature, usage } = action.payload
      state.limitations[feature] = usage
    },
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    builder
      .addMatcher(userLimitationsApi.endpoints.getUserLimitations.matchPending, state => {
        state.error = undefined
      })
      .addMatcher(
        userLimitationsApi.endpoints.getUserLimitations.matchFulfilled,
        (state, action) => {
          state.limitations = action.payload.limitations
          state.loading = false
          state.error = undefined
        }
      )
      .addMatcher(
        userLimitationsApi.endpoints.getUserLimitations.matchRejected,
        (state, action) => {
          state.loading = false
          state.error = action.error?.message || 'Failed to fetch user limitations'
        }
      )
  },
})

export const { updateUsage } = userLimitationsSlice.actions

export const userLimitationsReducer = userLimitationsSlice.reducer

// Selectors
export const selectUserLimitations = (state: RootState) => state.userLimitations

export const selectFeatureLimitation = createSelector(
  selectUserLimitations,
  (_: RootState, feature: Feature) => feature,
  (userLimitations, feature) => userLimitations.limitations[feature]
)

export const selectIsFeatureAllowed = createSelector(
  selectFeatureLimitation,
  featureLimitation => featureLimitation?.isAllowed ?? true
)
